import { autoUpdate, computePosition, flip, size } from '@floating-ui/dom';
import { TmElement, watch } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import {
  customElement,
  property,
  query,
  queryAssignedElements,
} from 'lit/decorators.js';
import { tagName } from '../../helpers/element.helper';
import { styles } from './dropdown.styles';

const tag = tagName('dropdown');

@customElement(tag)
export class DropdownComponent extends TmElement {
  static override styles = styles;

  @property({ type: Boolean })
  open = false;

  @query('.trigger')
  triggerElement!: HTMLElement;

  @query('.content')
  contentElement!: HTMLElement;

  @queryAssignedElements({
    slot: 'trigger',
    selector: 'a-button, a-icon-button',
  })
  triggerButtons!: Array<HTMLElement>;

  @property({ type: String })
  placement = 'bottom';

  @property({ type: Boolean, reflect: true })
  disabled?: boolean;

  cleanup?: () => void;

  @watch('open', { waitUntilFirstUpdate: true })
  handleOpenChange() {
    if (this.open) {
      this.#addOpenListeners();
      this.dispatchEvent(
        new CustomEvent('dropdown-opened', { bubbles: true, composed: true }),
      );

      this.triggerButtons.forEach((button) => {
        button.setAttribute('active', 'true');
      });
    } else {
      this.#removeOpenListeners();
      this.dispatchEvent(
        new CustomEvent('dropdown-closed', { bubbles: true, composed: true }),
      );

      this.triggerButtons.forEach((button) => {
        button.removeAttribute('active');
      });
    }
  }

  override render() {
    return html`
      <slot
        name="trigger"
        class="trigger"
        part="trigger"
        data-test="trigger"
        @click=${this.toggle}
        @keydown=${this.handleKeyDown}
        tabindex=${this.disabled ? '-1' : '0'}
      ></slot>
      <div popover class="content">
        <slot></slot>
      </div>
    `;
  }

  toggle() {
    if (this.disabled) {
      return;
    }

    this.open = !this.open;
    this.contentElement.togglePopover(this.open);
  }

  show() {
    this.open = true;
    this.contentElement.showPopover();
  }

  hide() {
    this.open = false;
    this.contentElement.hidePopover();
  }

  /**
   * Update the position of the dropdown content based on the trigger element using Floating UI.
   */
  updatePosition() {
    if (!this.open) {
      return;
    }

    computePosition(this.triggerElement, this.contentElement, {
      placement: this.placement === 'top' ? 'top-start' : 'bottom-start',
      middleware: [
        size({
          apply: ({ availableHeight, elements }) => {
            const reference = elements.reference as HTMLElement;

            this.style.setProperty(
              '--auto-size-available-height',
              `${availableHeight - 16}px`,
            );

            this.style.setProperty(
              '--reference-width',
              `${reference.clientWidth}px`,
            );
          },
        }),
        flip(),
      ],
    }).then(({ x, y }) => {
      Object.assign(this.contentElement.style, {
        'inset-inline-start': `${x}px`,
        'inset-block-start': `${y - window.scrollY}px`,
      });
    });
  }

  handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.toggle();
    }
  }

  override async connectedCallback() {
    super.connectedCallback();

    await this.updateComplete;

    this.cleanup = autoUpdate(
      this.triggerElement,
      this.contentElement,
      this.updatePosition.bind(this),
    );
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup?.();
    this.hide();
  }

  #handleDocumentEvent = (event: Event) => {
    const path = event.composedPath();
    if (
      (this && !path.includes(this)) ||
      (event instanceof KeyboardEvent && event.key === 'Escape')
    ) {
      this.hide();
    }
  };

  #addOpenListeners() {
    document.addEventListener('focusin', this.#handleDocumentEvent);
    document.addEventListener('mousedown', this.#handleDocumentEvent);
    document.addEventListener('keydown', this.#handleDocumentEvent);
  }

  #removeOpenListeners() {
    document.removeEventListener('focusin', this.#handleDocumentEvent);
    document.removeEventListener('mousedown', this.#handleDocumentEvent);
    document.removeEventListener('keydown', this.#handleDocumentEvent);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-dropdown': DropdownComponent;
  }
}
